import { ConvexHttpClient } from "convex/browser";
import { api } from "../../../convex/_generated/api.js";
import chalk from "chalk";

let _client: ConvexHttpClient | null = null;
let _schemaVerified: boolean | null = null;

/**
 * Get a Convex HTTP client, or null if CONVEX_URL is not set.
 */
export function getConvexClient(): ConvexHttpClient | null {
  if (_client) return _client;

  const url = process.env.CONVEX_URL;
  if (!url) return null;

  _client = new ConvexHttpClient(url);
  return _client;
}

/**
 * Check if the Convex deployment has fathom's schema.
 * Probes once by calling projects:getByName, caches the result.
 * Returns false if the function doesn't exist or errors.
 */
async function isSchemaDeployed(): Promise<boolean> {
  if (_schemaVerified !== null) return _schemaVerified;

  const client = getConvexClient();
  if (!client) {
    _schemaVerified = false;
    return false;
  }

  try {
    await client.query(api.projects.getByName, { name: "__probe__" });
    _schemaVerified = true;
  } catch {
    _schemaVerified = false;
  }
  return _schemaVerified;
}

/**
 * Try to sync to Convex. Returns false if not connected or schema not deployed.
 */
export function isConvexConnected(): boolean {
  return !!process.env.CONVEX_URL;
}

/**
 * Log a dim message about Convex sync status.
 */
export function logConvexStatus(synced: boolean): void {
  if (synced) {
    console.log(chalk.dim("  Synced to Convex."));
  } else if (!process.env.CONVEX_URL) {
    console.log(
      chalk.dim("  Convex sync skipped (set CONVEX_URL to enable)."),
    );
  }
  // If CONVEX_URL is set but sync failed, stay silent — already logged once
}

// ── Project helpers ────────────────────────────────────────

/**
 * Get or create a project by name. Returns the project ID, or null
 * if the Convex deployment doesn't have fathom's schema.
 */
export async function getOrCreateProject(
  client: ConvexHttpClient,
  name: string,
  clientName?: string,
): Promise<string | null> {
  if (!(await isSchemaDeployed())) return null;

  // Try to find existing project
  const existing = await client.query(api.projects.getByName, { name });
  if (existing) return existing._id;

  // Create new project
  return await client.mutation(api.projects.create, {
    name,
    client: clientName,
  });
}

// ── Estimate sync ──────────────────────────────────────────

export interface EstimateSyncData {
  projectName: string;
  specHash: string;
  features: Array<{
    id: string;
    name: string;
    category: string;
    complexity: string;
    estimatedInputTokens: number;
    estimatedOutputTokens: number;
    estimatedCost: number;
    recommendedModel: string;
    taskTypes: string[];
    overheadBreakdown: {
      contextResend: number;
      toolCalls: number;
      thinking: number;
      errorRecovery: number;
      planning: number;
      review: number;
    };
  }>;
  totalEstimate: {
    tokens: number;
    cost: number;
    optimistic: number;
    pessimistic: number;
    allFrontier: number;
  };
}

export async function syncEstimate(data: EstimateSyncData): Promise<boolean> {
  const client = getConvexClient();
  if (!client) return false;

  try {
    const projectId = await getOrCreateProject(client, data.projectName);
    if (!projectId) return false;
    await client.mutation(api.estimates.create, {
      projectId: projectId as any,
      specHash: data.specHash,
      features: data.features,
      totalEstimate: data.totalEstimate,
      calibrationSource: "default",
    });
    return true;
  } catch (err) {
    console.error(chalk.dim(`  Convex sync error: ${(err as Error).message}`));
    return false;
  }
}

// ── Actuals sync ───────────────────────────────────────────

export interface ActualSyncData {
  projectName: string;
  sessionId: string;
  provider: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
  cacheCreateTokens: number;
  toolCalls: number;
  filesTouched?: string[];
  estimatedCost: number;
  sessionStart: number;
  sessionEnd?: number;
  activeMinutes?: number;
  featureId?: string;
  tagConfidence?: number;
  tagSource?: string;
  tagStatus?: string;
}

export async function syncActual(data: ActualSyncData): Promise<boolean> {
  const client = getConvexClient();
  if (!client) return false;

  try {
    const projectId = await getOrCreateProject(client, data.projectName);
    if (!projectId) return false;
    await client.mutation(api.actuals.record, {
      projectId: projectId as any,
      sessionId: data.sessionId,
      provider: data.provider,
      model: data.model,
      inputTokens: data.inputTokens,
      outputTokens: data.outputTokens,
      cacheReadTokens: data.cacheReadTokens,
      cacheCreateTokens: data.cacheCreateTokens,
      toolCalls: data.toolCalls,
      filesTouched: data.filesTouched,
      estimatedCost: data.estimatedCost,
      sessionStart: data.sessionStart,
      sessionEnd: data.sessionEnd,
      activeMinutes: data.activeMinutes,
      featureId: data.featureId,
      tagConfidence: data.tagConfidence,
      tagSource: data.tagSource,
      tagStatus: data.tagStatus,
    });
    return true;
  } catch (err) {
    console.error(chalk.dim(`  Convex sync error: ${(err as Error).message}`));
    return false;
  }
}

// ── Intake sync ────────────────────────────────────────────

export interface IntakeSyncData {
  projectName: string;
  rawInput: string;
  source: string;
  extractedSlices: Array<{
    sliceId: string;
    name: string;
    category: string;
    complexity: string;
    priority: string;
    description: string;
    estimatedTokens: number;
    estimatedCost: number;
    velocityBenchmark?: {
      avgSessions: number;
      avgDays: number;
      avgCost: number;
      dataPoints: number;
    };
    status: string;
  }>;
}

export async function syncIntake(data: IntakeSyncData): Promise<boolean> {
  const client = getConvexClient();
  if (!client) return false;

  try {
    const projectId = await getOrCreateProject(client, data.projectName);
    if (!projectId) return false;
    await client.mutation(api.intakes.create, {
      projectId: projectId as any,
      rawInput: data.rawInput,
      source: data.source,
      extractedSlices: data.extractedSlices,
    });
    return true;
  } catch (err) {
    console.error(chalk.dim(`  Convex sync error: ${(err as Error).message}`));
    return false;
  }
}

// ── Reconciliation sync ─────────────────────────────────────

export interface ReconciliationSyncData {
  projectName: string;
  featureDeltas: Array<{
    featureId: string;
    featureName: string;
    estimatedTokens: number;
    actualTokens: number;
    delta: number;
    accuracy: number;
    modelRecommended: string;
    modelUsed: string;
    sessionsCount: number;
    wallClockDays?: number;
    activeMinutes?: number;
    tagQuality: {
      autoTagged: number;
      likelyTagged: number;
      manualTagged: number;
    };
    insights: string[];
  }>;
  overallAccuracy: number;
  autoTagAccuracy: number;
  suggestedAdjustments: Array<{
    profileKey: string;
    currentValue: number;
    suggestedValue: number;
    dataPoints: number;
    confidence: number;
  }>;
}

export async function syncReconciliation(
  data: ReconciliationSyncData,
): Promise<boolean> {
  const client = getConvexClient();
  if (!client) return false;

  try {
    const projectId = await getOrCreateProject(client, data.projectName);
    if (!projectId) return false;

    // Get latest estimate for this project
    const estimate = await client.query(api.estimates.getByProject, {
      projectId: projectId as any,
    });
    if (!estimate) {
      console.error(chalk.dim("  No estimate found for reconciliation sync."));
      return false;
    }

    await client.mutation(api.reconciliations.create, {
      projectId: projectId as any,
      estimateId: estimate._id,
      featureDeltas: data.featureDeltas,
      overallAccuracy: data.overallAccuracy,
      autoTagAccuracy: data.autoTagAccuracy,
      suggestedAdjustments: data.suggestedAdjustments,
    });
    return true;
  } catch (err) {
    console.error(chalk.dim(`  Convex sync error: ${(err as Error).message}`));
    return false;
  }
}

// ── Calibration sync ────────────────────────────────────────

export interface CalibrationSyncData {
  profiles: Array<{
    taskType: string;
    baseInput: number;
    baseOutput: number;
    overheadMultipliers: {
      contextResend: number;
      toolCalls: number;
      thinking: number;
      errorRecovery: number;
      planning: number;
      review: number;
    };
    dataPoints: number;
    confidence: number;
  }>;
}

export async function syncCalibration(
  data: CalibrationSyncData,
): Promise<boolean> {
  const client = getConvexClient();
  if (!client) return false;
  if (!(await isSchemaDeployed())) return false;

  try {
    for (const profile of data.profiles) {
      await client.mutation(api.calibratedProfiles.upsert, profile);
    }
    return true;
  } catch (err) {
    console.error(chalk.dim(`  Convex sync error: ${(err as Error).message}`));
    return false;
  }
}

// ── Velocity sync ───────────────────────────────────────────

export interface VelocitySyncData {
  projectName: string;
  period: string;
  featuresCompleted: number;
  totalTokens: number;
  totalCost: number;
  avgEstimateAccuracy: number;
  avgCostPerFeature: number;
  avgDaysPerFeature: number;
  cacheHitRate: number;
  autoTagRate: number;
  benchmarks?: Array<{
    category: string;
    complexity: string;
    avgTokens: number;
    avgCost: number;
    avgSessions: number;
    avgWallClockDays: number;
    avgActiveMinutes: number;
    p25Days: number;
    p75Days: number;
    dataPoints: number;
    confidence: number;
  }>;
}

export async function syncVelocity(
  data: VelocitySyncData,
): Promise<boolean> {
  const client = getConvexClient();
  if (!client) return false;

  try {
    const projectId = await getOrCreateProject(client, data.projectName);
    if (!projectId) return false;

    // Record snapshot
    await client.mutation(api.velocity.recordSnapshot, {
      projectId: projectId as any,
      period: data.period,
      featuresCompleted: data.featuresCompleted,
      totalTokens: data.totalTokens,
      totalCost: data.totalCost,
      avgEstimateAccuracy: data.avgEstimateAccuracy,
      avgCostPerFeature: data.avgCostPerFeature,
      avgDaysPerFeature: data.avgDaysPerFeature,
      cacheHitRate: data.cacheHitRate,
      autoTagRate: data.autoTagRate,
    });

    // Upsert benchmarks
    if (data.benchmarks) {
      for (const b of data.benchmarks) {
        await client.mutation(api.velocity.upsertBenchmark, b);
      }
    }

    return true;
  } catch (err) {
    console.error(chalk.dim(`  Convex sync error: ${(err as Error).message}`));
    return false;
  }
}
